(() => {
  const CNSTRC = {
    DEFAULT_POD: "minicart-upsell",
    SECTION: "Products",
    CACHE_TTL: 5 * 60 * 1000,
  };

  const escapeHTML = (s = "") =>
    String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");

  const bySel = (sel, root = document) => root.querySelector(sel);

  const imageDimsFor = (orientation) => {
    switch (orientation) {
      case "square":
        return { w: 460, h: 460 };
      case "landscape":
        return { w: 200, h: 160 };
      case "portrait":
        return { w: 160, h: 200 };
      default:
        return { w: 460, h: 460 };
    }
  };

  const money = (cents, currency = window.theme?.fakeDiscount?.currentMarket?.currency || "USD") => {
    try {
      return new Intl.NumberFormat(window.Shopify?.locale || "en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format((Math.round(cents) || 0) / 100);
    } catch {
      return `${(Math.round(cents) || 0) / 100}`;
    }
  };

  const productHasTag = (product, requiredTag) => {
    if (!product.tags || !requiredTag) return false;
    const tags = Array.isArray(product.tags) ? product.tags : (product.tags || "").split(",").map((tag) => tag.trim());
    return tags.includes(requiredTag);
  };

  /**
   * Update price display when swatch changes
   * @param {Element} productItem - Product card container
   * @param {Element} swatch - Selected swatch button
   */
  const updateSwatchPrice = (productItem, swatch) => {
    const priceContainer = productItem.querySelector("[data-product-item-price]");
    if (!priceContainer) return;

    const price = parseFloat(swatch.getAttribute("data-price")) || 0;
    const compareAtPrice = parseFloat(swatch.getAttribute("data-compare-at-price")) || 0;

    if (price <= 0) return; // No valid price data

    // Use ConstructorUtils if available, otherwise use local money function
    const formatPrice = (amount) => {
      if (window.ConstructorUtils?.formatMoney) {
        return window.ConstructorUtils.formatMoney(amount);
      }
      return money(amount * 100); // Convert to cents for local money function
    };

    const formattedPrice = formatPrice(price);
    const formattedComparePrice = formatPrice(compareAtPrice);
    const isOnSale = compareAtPrice > price;
    const isReverse = !!priceContainer.querySelector(".product-item-price-comp-value");

    const config = window.ConstructorUtils?.getCachedThemeConfig?.() || {};
    const priceRegularLabel = config.productPriceRegular || "Regular price";
    const priceSaleLabel = config.productPriceSale || "Sale price";

    if (isReverse) {
      const compValueLabel = window.theme?.product?.compValue || "Comp. value";
      priceContainer.innerHTML = `
        <span class="product-item-price-final">${formattedPrice}</span>
        ${
          compareAtPrice > price
            ? `<span class="product-item-price-comp-value text-muted ms-1">${formattedComparePrice} ${compValueLabel}</span>`
            : ""
        }
      `;
    } else if (isOnSale) {
      priceContainer.innerHTML = `
        <span class="product-item-price-compare text-muted me-1">
          <span class="visually-hidden">${priceRegularLabel}&nbsp;</span>
          <s>${formattedComparePrice}</s>
        </span>
        <span class="product-item-price-final">
          <span class="visually-hidden">${priceSaleLabel}&nbsp;</span>
          ${formattedPrice}
        </span>
      `;
    } else {
      priceContainer.innerHTML = `
        <span class="product-item-price-final">
          ${formattedPrice}
        </span>
      `;
    }
  };

  class RecsService {
    constructor(clientResolver) {
      this.clientResolver = clientResolver;
      this.cache = new Map();
      this.inflight = new Map();
    }

    _key(podId, params) {
      const { numResults = 8, filters, item_id } = params || {};
      return `${podId}:${numResults}:${item_id || "no-item"}:${JSON.stringify(filters || {})}`;
    }

    async get(podId, params = {}) {
      const key = this._key(podId, params);

      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.ts < CNSTRC.CACHE_TTL) {
        return cached;
      }

      if (this.inflight.has(key)) return this.inflight.get(key);

      const p = this._fetch(podId, params)
        .then((res) => {
          this.cache.set(key, { ...res, ts: Date.now() });
          this.inflight.delete(key);
          return this.cache.get(key);
        })
        .catch((e) => {
          this.inflight.delete(key);
          throw e;
        });

      this.inflight.set(key, p);
      return p;
    }

    async _fetch(podId, params) {
      const client = await this.clientResolver();
      const {
        numResults = 8,
        filters,
        fmt_options = {},
        variationsMap,
        hiddenFields,
        preFilterExpression,
        section = CNSTRC.SECTION,
        itemIds,
      } = params;

      const networkParameters = { timeout: 5000 };

      const defaultFmtOptions = {
        show_hidden_fields: false,
        show_hidden_facets: false,
        fields: [
          "data.handle",
          "data.title",
          "data.price",
          "data.compareAtPrice",
          "data.image_url",
          "data.inventory_quantity",
          "data.group_ids",
          "data.preorder_tag",
          "data.new_preorder_tag",
          "data.waterproof_tag",
          "data.bestseller_tag",
          "data.new_tag",
          "data.mediaImages",
          "data.url",
          "data.tags",
          "data.vendor",
          "data.product_type",
          "data.options",
          "data.fake_discount_percentage",
          "data.fake_discount_message",
          "data.created_at",
          "data.updated_at",
          "data.published_at",
          "data.created_date",
          "variations",
        ],
        ...fmt_options,
      };

      const recommendationParams = {
        numResults,
        section,
        filters,
        fmt_options: defaultFmtOptions,
        variationsMap,
        hiddenFields,
        preFilterExpression,
      };

      if (itemIds) {
        recommendationParams.itemIds = itemIds;
      }

      const response = await client.recommendations.getRecommendations(podId, recommendationParams, networkParameters);
      const results = response?.response?.results ?? [];
      const resultId = response?.result_id || (results[0] && results[0].result_id) || "";
      const total = response?.response?.total_num_results ?? results.length;

      return { data: results, meta: { resultId, total } };
    }
  }

  const toCardModel = (item) => {
    const data = item.data || {};
    const variations = item.variations || [];
    const title = item.value || data.title || "Product";
    const price = data.price || 0;
    const compareAtPrice = data.compareAtPrice || 0;
    const url = data.url || (data.handle ? `/products/${data.handle}` : "");

    let handle = null;
    if (data.url) {
      const urlParts = data.url.split("/products/");
      if (urlParts.length > 1) {
        handle = urlParts[1].split("?")[0].split("#")[0];
      }
    }
    if (!handle) {
      handle =
        data.handle ||
        data.slug ||
        data.shopify_handle ||
        item.value ||
        (data.id && typeof data.id === "string" && /[a-zA-Z-]/.test(data.id) ? data.id : null);
    }

    const options = (() => {
      const optionsMap = new Map();
      let hasExplicitOptions = false;

      variations.forEach((variation) => {
        const varData = variation.data || {};

        if (varData.color) {
          hasExplicitOptions = true;
          if (!optionsMap.has("Color")) {
            optionsMap.set("Color", { name: "Color", values: new Set() });
          }
          optionsMap.get("Color").values.add(varData.color);
        }
        if (varData.size) {
          hasExplicitOptions = true;
          if (!optionsMap.has("Size")) {
            optionsMap.set("Size", { name: "Size", values: new Set() });
          }
          optionsMap.get("Size").values.add(varData.size);
        }
      });

      if (!hasExplicitOptions && variations.length > 0) {
        variations.forEach((variation) => {
          const varData = variation.data || {};
          const title = varData.title || "";

          if (title) {
            if (title.includes(" / ")) {
              // Multi-option format: "Gold / Clear"
              const parts = title.split(" / ").map((part) => part.trim());

              if (parts[0]) {
                if (!optionsMap.has("Option1")) {
                  optionsMap.set("Option1", { name: "Option1", values: new Set() });
                }
                optionsMap.get("Option1").values.add(parts[0]);
              }

              if (parts[1]) {
                if (!optionsMap.has("Option2")) {
                  optionsMap.set("Option2", { name: "Option2", values: new Set() });
                }
                optionsMap.get("Option2").values.add(parts[1]);
              }
            } else {
              // Single-option format: "Silver Waterproof", "18K Gold Waterproof"
              if (!optionsMap.has("Option1")) {
                optionsMap.set("Option1", { name: "Option1", values: new Set() });
              }
              optionsMap.get("Option1").values.add(title.trim());
            }
          }
        });
      }

      return Array.from(optionsMap.entries()).map(([name, option]) => ({
        name,
        values: Array.from(option.values),
      }));
    })();

    const variants =
      variations?.map((v) => {
        const varData = v.data || {};
        const variantTitle = varData.title || v.value || "Variant";

        let color = varData.color;
        let size = varData.size;
        let option1, option2;

        if (!color && !size && variantTitle) {
          if (variantTitle.includes(" / ")) {
            const parts = variantTitle.split(" / ").map((part) => part.trim());
            option1 = parts[0] || undefined;
            option2 = parts[1] || undefined;
          } else {
            option1 = variantTitle.trim();
          }
        }

        return {
          id: varData.variation_id || v.id,
          title: variantTitle,
          price: varData.price || data.price || 0,
          compare_at_price: varData.compareAtPrice || data.compareAtPrice || 0,
          inventory_quantity: varData.inventory_quantity || 0,
          available:
            varData.inventory_policy === "continue" ||
            varData.inventory_policy === "CONTINUE" ||
            varData.inventoryPolicy === "continue" ||
            varData.inventoryPolicy === "CONTINUE" ||
            (varData.inventory_quantity || 0) > 0,
          image_url: varData.image_url,
          color: color,
          size: size,
          option1: option1,
          option2: option2,
          discountPercentage: varData.discountPercentage || 0,
          inventory_policy: varData.inventory_policy || varData.inventoryPolicy || "deny",
        };
      }) || [];

    return {
      id: data.id || item.id || handle,
      handle,
      title,
      price: price,
      compare_at_price: compareAtPrice,
      featured_image: {
        alt: title,
        src: data.image_url || "",
      },
      images: (() => {
        const rawMedia = data.mediaImages;
        const mediaArray = Array.isArray(rawMedia) ? rawMedia : rawMedia ? [rawMedia] : [];
        return mediaArray.map((img) => ({
          alt: img?.altText || title,
          src: img?.transformedSrc || img?.src || "",
        }));
      })(),
      available:
        data.inventory_policy === "continue" ||
        data.inventory_policy === "CONTINUE" ||
        data.inventoryPolicy === "continue" ||
        data.inventoryPolicy === "CONTINUE" ||
        (data.inventory_quantity || 0) > 0,
      vendor: data.vendor || "",
      product_type: data.product_type || "",
      has_only_default_variant: variations.length <= 1,
      options,
      variants,
      tags: [
        ...(data.group_ids || []),
        ...(Array.isArray(data.tags) ? data.tags : data.tags ? data.tags.split(",").map((t) => t.trim()) : []),
        ...(data.preorder_tag ? ["preorder"] : []),
        ...(data.waterproof_tag ? ["waterproof"] : []),
        ...(data.bestseller_tag ? ["bestseller", "bestsellers"] : []),
        ...(data.new_tag ? ["new"] : []),
        ...(data.low_inventory_tag ? ["low-inventory"] : []),
      ],
      fakeDiscountPercentage: data.fake_discount_percentage || 0,
      fakeDiscountMessage: data.fake_discount_message || "",
      strategy: item.strategy,
      matchedTerms: item.matched_terms || [],
      url,
      constructorData: data,
      variations,
    };
  };

  class ConstructorRecommendations {
    constructor() {
      this.client = null;
      this.isInitialized = false;
      this.currentRequests = new Map();
      this.retryCount = 0;
      this.maxRetries = 50;
      this.cache = new Map();
      this.cacheTTL = 5 * 60 * 1000;

      this.initializeWhenReady();
      this.bindEvents();
    }

    buildSizedImageUrl(src, width, height) {
      if (!src) return "";

      if (src.includes("width=") || src.includes("height=") || src.includes("_x")) {
        return src;
      }

      const separator = src.includes("?") ? "&" : "?";
      return `${src}${separator}width=${width}&height=${height}&crop=center`;
    }

    async initializeWhenReady() {
      try {
        await this.initializeConstructor();
        this.isInitialized = true;
      } catch (error) {
        console.warn("Constructor Recommendations failed to initialize:", error);
      }
    }

    bindEvents() {
      document.addEventListener("click", (e) => {
        const swatch = e.target.closest('[data-action="change-swatch"]');
        if (!swatch) return;

        const productItem = swatch.closest(".product-item");
        if (!productItem) return;

        // Check if this is a recommendation product item
        const isRecommendation = productItem.closest("[data-constructor-recommendations]");
        if (!isRecommendation) return;

        e.preventDefault();
        e.stopPropagation();

        const allSwatches = productItem.querySelectorAll(".color-swatches .btn");
        allSwatches.forEach((s) => s.classList.remove("selected"));
        swatch.classList.add("selected");

        const img = productItem.querySelector(".product-item-img--primary");
        if (img) {
          const rawSrc = swatch.getAttribute("data-img");

          if (rawSrc) {
            const width = parseInt(swatch.getAttribute("data-img-width")) || 600;
            const height = parseInt(swatch.getAttribute("data-img-height")) || 600;
            const baseUrl = rawSrc.split("?")[0];

            img.src = `${baseUrl}?width=${width}&height=${height}`;
            img.srcset = `${baseUrl}?width=${width}&height=${height} 1x, ${baseUrl}?width=${width * 2}&height=${height * 2} 2x`;
          }
        }

        // Update price display
        updateSwatchPrice(productItem, swatch);
      });
    }

    getProductId() {
      return window.productId || null;
    }

    async initializeConstructor() {
      try {
        this.client = window.constructorio;
        this.initializeRecommendationSections();
      } catch (error) {
        console.warn("Constructor Recommendations initialization failed:", error);
      }
    }

    initializeRecommendationSections() {
      const sections = document.querySelectorAll("[data-constructor-recommendations]");
      sections.forEach((section) => {
        const podId = section.dataset.constructorRecommendations;
        const numResults = parseInt(section.dataset.numResults) || 8;

        if (podId) {
          this.loadRecommendations(podId, {
            section: section,
            numResults: numResults,
          });
        }
      });
    }

    async loadRecommendations(podId, options = {}) {
      if (!this.isInitialized || !this.client) {
        setTimeout(() => {
          if (this.isInitialized && this.client) {
            this.loadRecommendations(podId, options);
          }
        }, 500);
        return false;
      }

      const currentProductId = this.getProductId();
      const cacheKey = `${podId}-${options.numResults || 8}-${currentProductId || "no-item"}`;
      const cached = this.getCachedResult(cacheKey);
      if (cached) {
        this.renderRecommendations(cached.data, { ...options, podId });
        return true;
      }

      if (this.currentRequests.has(podId)) {
        this.currentRequests.get(podId).abort();
        this.currentRequests.delete(podId);
      }

      try {
        this.showLoadingState(options.section);

        const controller = new AbortController();
        this.currentRequests.set(podId, controller);

        const requestParams = {
          numResults: options.numResults || 8,
          section: "Products",
          fmt_options: {
            show_hidden_facets: false,
            show_hidden_fields: false,
            fields: [
              "data.handle",
              "data.title",
              "data.price",
              "data.compareAtPrice",
              "data.image_url",
              "data.inventory_quantity",
              "data.group_ids",
              "data.preorder_tag",
              "data.new_preorder_tag",
              "data.waterproof_tag",
              "data.bestseller_tag",
              "data.new_tag",
              "data.mediaImages",
              "data.url",
              "data.tags",
              "data.vendor",
              "data.product_type",
              "data.options",
              "data.fake_discount_percentage",
              "data.fake_discount_message",
              "data.created_at",
              "data.updated_at",
              "data.published_at",
              "data.created_date",
              "data.id",
              "variations",
            ],
          },
          ...(currentProductId && { itemIds: [currentProductId] }),
          ...options.filters,
        };

        let response;
        try {
          response = await this.client.recommendations.getRecommendations(podId, requestParams, {
            timeout: 5000,
            signal: controller.signal,
          });
        } catch (apiError) {
          throw apiError;
        }

        if (controller.signal.aborted) {
          return false;
        }

        this.currentRequests.delete(podId);

        const results = response.response?.results || response.results || [];
        const resultId = response.result_id || response.response?.result_id;

        if (results.length > 0) {
        }

        this.setCachedResult(cacheKey, {
          data: results,
          response: response,
          timestamp: Date.now(),
        });

        await this.renderRecommendations(results, {
          ...options,
          podId,
          resultId: resultId,
          totalResults: response.response?.total_num_results || results.length,
        });

        return true;
      } catch (error) {
        this.currentRequests.delete(podId);
        this.hideLoadingState(options.section);
        this.showErrorState(options.section);
        return false;
      }
    }

    async renderRecommendations(results, options = {}) {
      if (!results || results.length === 0) {
        this.showNoResults(options.section, options.podId);
        return;
      }

      try {
        const constructorProducts = results
          .map((item, index) => {
            let handle = null;

            if (item.data?.url) {
              const urlParts = item.data.url.split("/products/");
              if (urlParts.length > 1) {
                handle = urlParts[1].split("?")[0].split("#")[0];
              }
            }

            if (!handle) {
              handle =
                item.data?.handle ||
                item.data?.slug ||
                item.data?.shopify_handle ||
                item.value ||
                (item.data?.id && typeof item.data.id === "string" && /[a-zA-Z-]/.test(item.data.id) ? item.data.id : null);
            }

            return {
              handle: handle,
              constructorData: item.data,
              strategy: item.strategy,
              variations: item.variations || [],
              value: item.value,
              matchedTerms: item.matched_terms || [],
              isSlotted: item.is_slotted || false,
            };
          })
          .filter((item) => item.handle);

        const productHandles = constructorProducts.map((item) => item.handle);

        if (productHandles.length === 0) {
          this.showNoResults(options.section, options.podId);
          return;
        }

        const combinedProducts = [];
        for (const constructorProduct of constructorProducts) {
          try {
            const data = constructorProduct.constructorData || {};
            const variations = constructorProduct.variations || [];

            const productTitle = constructorProduct.value || data.title || "Product";

            const product = {
              handle: constructorProduct.handle,
              title: productTitle,
              price: data.price || 0,
              compare_at_price: data.compareAtPrice || 0,
              featured_image: {
                alt: productTitle,
                src: data.image_url || "",
              },
              images: (() => {
                const rawMedia = data.mediaImages;
                const mediaArray = Array.isArray(rawMedia) ? rawMedia : rawMedia ? [rawMedia] : [];
                return mediaArray.map((img) => ({
                  alt: img?.altText || productTitle,
                  src: img?.transformedSrc || img?.src || "",
                }));
              })(),
              available:
                data.inventory_policy === "continue" ||
                data.inventory_policy === "CONTINUE" ||
                data.inventoryPolicy === "continue" ||
                data.inventoryPolicy === "CONTINUE" ||
                (data.inventory_quantity || 0) > 0,
              vendor: data.vendor || "",
              product_type: data.product_type || "",
              collections: [],
              has_only_default_variant: variations.length <= 1,
              options: (() => {
                const optionsMap = new Map();
                let hasExplicitOptions = false;

                variations.forEach((variation) => {
                  const varData = variation.data || {};

                  if (varData.color) {
                    hasExplicitOptions = true;
                    if (!optionsMap.has("Color")) {
                      optionsMap.set("Color", { name: "Color", values: new Set() });
                    }
                    optionsMap.get("Color").values.add(varData.color);
                  }
                  if (varData.size) {
                    hasExplicitOptions = true;
                    if (!optionsMap.has("Size")) {
                      optionsMap.set("Size", { name: "Size", values: new Set() });
                    }
                    optionsMap.get("Size").values.add(varData.size);
                  }
                });

                if (!hasExplicitOptions && variations.length > 0) {
                  variations.forEach((variation) => {
                    const varData = variation.data || {};
                    const title = varData.title || "";

                    if (title) {
                      if (title.includes(" / ")) {
                        // Multi-option format: "Gold / Clear"
                        const parts = title.split(" / ").map((part) => part.trim());

                        if (parts[0]) {
                          if (!optionsMap.has("Option1")) {
                            optionsMap.set("Option1", { name: "Option1", values: new Set() });
                          }
                          optionsMap.get("Option1").values.add(parts[0]);
                        }

                        if (parts[1]) {
                          if (!optionsMap.has("Option2")) {
                            optionsMap.set("Option2", { name: "Option2", values: new Set() });
                          }
                          optionsMap.get("Option2").values.add(parts[1]);
                        }
                      } else {
                        // Single-option format: "Silver Waterproof", "18K Gold Waterproof"
                        if (!optionsMap.has("Option1")) {
                          optionsMap.set("Option1", { name: "Option1", values: new Set() });
                        }
                        optionsMap.get("Option1").values.add(title.trim());
                      }
                    }
                  });
                }

                return Array.from(optionsMap.entries()).map(([name, option]) => ({
                  name,
                  values: Array.from(option.values),
                }));
              })(),
              variants:
                variations?.map((v) => {
                  const varData = v.data || {};
                  const variantTitle = varData.title || v.value || "Variant";

                  let color = varData.color;
                  let size = varData.size;
                  let option1, option2;

                  if (!color && !size && variantTitle) {
                    if (variantTitle.includes(" / ")) {
                      // Multi-option format: "Gold / Clear"
                      const parts = variantTitle.split(" / ").map((part) => part.trim());
                      option1 = parts[0] || undefined;
                      option2 = parts[1] || undefined;
                    } else {
                      // Single-option format: "Silver Waterproof", "18K Gold Waterproof"
                      option1 = variantTitle.trim();
                    }
                  }

                  return {
                    id: varData.variation_id || v.id,
                    title: variantTitle,
                    price: varData.price || data.price || 0,
                    compare_at_price: varData.compareAtPrice || data.compareAtPrice || 0,
                    inventory_quantity: varData.inventory_quantity || 0,
                    available:
                      varData.inventory_policy === "continue" ||
                      varData.inventory_policy === "CONTINUE" ||
                      varData.inventoryPolicy === "continue" ||
                      varData.inventoryPolicy === "CONTINUE" ||
                      (varData.inventory_quantity || 0) > 0,
                    image_url: varData.image_url,
                    color: color,
                    size: size,
                    option1: option1,
                    option2: option2,
                    discountPercentage: varData.discountPercentage || 0,
                    inventory_policy: varData.inventory_policy || varData.inventoryPolicy || "deny",
                  };
                }) || [],

              constructorData: constructorProduct.constructorData || constructorProduct,
              variations: variations,
              matchedTerms: constructorProduct.matched_terms || [],
              isSlotted: constructorProduct.is_slotted || false,
              url: data.url || `/products/${constructorProduct.handle}`,

              tags: [
                ...(data.group_ids || []),
                ...(Array.isArray(data.tags) ? data.tags : data.tags ? data.tags.split(",").map((t) => t.trim()) : []),
                ...(data.preorder_tag ? ["preorder"] : []),
                ...(data.waterproof_tag ? ["waterproof"] : []),
                ...(data.bestseller_tag ? ["bestseller", "bestsellers"] : []),
                ...(data.new_tag ? ["new"] : []),
                ...(data.low_inventory_tag ? ["low-inventory"] : []),
              ],

              isPreorder: data.preorder_tag || false,
              isWaterproof: data.waterproof_tag || false,
              isBestseller: data.bestseller_tag || false,
              isNew: data.new_tag || false,
              isLowInventory: data.low_inventory_tag || false,

              fakeDiscountPercentage: data.fake_discount_percentage || 0,
              fakeDiscountMessage: data.fake_discount_message || "",
              data: constructorProduct,
              strategy: constructorProduct.strategy,
            };

            combinedProducts.push(product);
          } catch (error) {
            console.warn("Skipping bad product item:", error, constructorProduct);
          }
        }

        const productHTML = this.renderProductsTemplate(combinedProducts, options);

        const container = options.section || document.querySelector(`[data-constructor-recommendations="${options.podId}"]`);

        if (!container) {
          return;
        }

        if (!container.closest(".featured-products")) {
          const parentSection = container.closest("section") || container.parentElement;
          if (parentSection && !parentSection.classList.contains("featured-products")) {
            parentSection.classList.add("featured-products");
          }
        }

        const productList = container.querySelector(".product-list");
        if (productList) {
          productList.innerHTML = productHTML;
          productList.classList.add("constructor-loaded");

          productList.setAttribute("data-cnstrc-recommendations", "");
          productList.setAttribute("data-cnstrc-recommendations-pod-id", options.podId || "");
          productList.setAttribute("data-cnstrc-result-id", options.resultId || "");
          productList.setAttribute("data-cnstrc-num-results", combinedProducts.length.toString());

          document.dispatchEvent(
            new CustomEvent("constructor:recommendations:loaded", {
              detail: {
                numResults: combinedProducts.length,
                podId: options.podId,
                section: options.sectionId || container.id,
              },
            })
          );
        }

        this.initializeProductTracking(container, options.podId, combinedProducts);
        this.reinitializeComponents(container);

        this.initializeSplideCarousel(container);
        this.injectConstructorCSS();

        setTimeout(() => {
          if (typeof initFeaturedProducts === "function") {
            initFeaturedProducts();
          }
        }, 200);

        this.hideLoadingState(options.section);
      } catch (error) {
        this.hideLoadingState(options.section);
        this.showErrorState(options.section);
      }
    }

    renderProductsTemplate(products, options = {}) {
      if (!products || products.length === 0) {
        return '<li class="col-12"><p class="text-center">No products found.</p></li>';
      }

      return products
        .map((product, index) => {
          return this.renderSingleProductTemplate(product, { ...options, index, productIndex: index });
        })
        .join("");
    }

    renderSingleProductTemplate(product, options = {}) {
      if (!product) return "";

      // Use getCachedThemeConfig like browse does for consistent wishlist/badge behavior
      const config = ConstructorUtils.getCachedThemeConfig();
      const strategyId = product.strategy?.id || product.strategy || "";

      return ConstructorUtils.renderProductCard(product, {
        ...options,
        config,
        strategyId,
        renderAddToCartDesktop: (prod) => this.getSmartAddToCartForm(prod),
        renderAddToCartMobile: (prod) => this.getSmartAddToCartFormMobile(prod),
      });
    }

    getSmartFakeDiscount(product) {
      if (product.fakeDiscountPercentage && product.fakeDiscountPercentage > 0) {
        const basePrice = product.price || 0;
        const discountAmount = (basePrice * product.fakeDiscountPercentage) / 100;
        const discountedPrice = Math.ceil(basePrice - discountAmount);

        const themeMessage = window.theme?.fakeDiscount?.marketSettings?.message || "";
        const finalMessage = product.fakeDiscountMessage || themeMessage || "";

        return {
          shouldApply: true,
          finalPrice: discountedPrice,
          originalPrice: product.compare_at_price || product.price || 0,
          message: finalMessage,
          messageColor: window.theme?.fakeDiscount?.marketSettings?.messageColor || "#ff0000",
          discountPercentage: product.fakeDiscountPercentage,
          discountType: "percentage",
        };
      }

      const fakeDiscountSettings = window.theme?.fakeDiscount;

      if (!fakeDiscountSettings?.marketSettings?.enabled) {
        return {
          shouldApply: false,
          finalPrice: product.price || 0,
          originalPrice: product.compare_at_price || 0,
          message: null,
        };
      }

      const settings = fakeDiscountSettings.marketSettings;

      const hasDiscountAllowedTag =
        this.productHasTag(product, "discount allowed") || this.productHasTag(product, "discount-allowed");
      const hasConfiguredTag = this.productHasTag(product, settings.tag);
      const hasHighDiscount =
        product.compare_at_price &&
        product.price &&
        ((product.compare_at_price - product.price) / product.compare_at_price) * 100 >= 50;

      if (!hasDiscountAllowedTag && !hasConfiguredTag && !hasHighDiscount) {
        return {
          shouldApply: false,
          finalPrice: product.price || 0,
          originalPrice: product.compare_at_price || 0,
          message: null,
        };
      }

      let basePrice = 0;
      const priceSource = fakeDiscountSettings.priceSource || "price";

      if (priceSource === "price") {
        basePrice = product.price || 0;
      } else {
        if (product.compare_at_price && product.compare_at_price > product.price) {
          basePrice = product.compare_at_price;
        } else {
          basePrice = product.price || 0;
        }
      }

      let discountedPrice = 0;
      let actualPercentageForMessage = settings.percentage;

      if (settings.type === "fixed") {
        discountedPrice = parseFloat(settings.fixedAmount || 0) * 100;
        if (basePrice > 0) {
          actualPercentageForMessage = Math.round(((basePrice - discountedPrice) * 100) / basePrice);
        }
      } else {
        const discountAmount = (basePrice * settings.percentage) / 100;
        discountedPrice = basePrice - discountAmount;
        actualPercentageForMessage = settings.percentage;
      }

      let message = settings.message || "";
      message = message.replace(/\[percentage\]/g, settings.percentage);
      message = message.replace(/\[percentage-amount\]/g, actualPercentageForMessage);
      message = message.replace(/\[amount\]/g, settings.fixedAmount || "0");
      message = message.replace(/\[code\]/g, settings.code || "");

      const displayOriginalPrice = product.compare_at_price || product.price || 0;
      const roundedFinalPrice = Math.ceil(discountedPrice);

      return {
        shouldApply: true,
        finalPrice: roundedFinalPrice,
        originalPrice: displayOriginalPrice,
        message: message,
        messageColor: settings.messageColor,
        discountPercentage: actualPercentageForMessage,
        discountType: settings.type || "percentage",
        fixedAmount: settings.fixedAmount,
        basePrice: basePrice,
      };
    }

    productHasTag(product, requiredTag) {
      if (!product.tags || !requiredTag) return false;
      const tags = Array.isArray(product.tags) ? product.tags : (product.tags || "").split(",").map((tag) => tag.trim());
      return tags.includes(requiredTag);
    }

    getSmartPrimaryBadge(product, isOnSale, hasDiscount) {
      const transformedProduct = {
        ...product,
        data: product.constructorData,
        constructorData: {
          data: product.constructorData,
        },
      };
      return ConstructorUtils.generateProductBadge(transformedProduct, hasDiscount);
    }

    /**
     * Generate responsive images with CDN optimization and srcset
     * @param {string} primarySrc - Primary image URL
     * @param {string} secondarySrc - Secondary image URL
     * @param {number} baseWidth - Base width for aspect ratio
     * @param {number} baseHeight - Base height for aspect ratio
     * @returns {Object} - Optimized image URLs and srcset
     */
    generateResponsiveImages(primarySrc, secondarySrc, baseWidth, baseHeight) {
      const cdnParams = "crop=center&format=webp&quality=80";

      const breakpoints = [
        { width: 300, condition: "(max-width: 480px)" },
        { width: 400, condition: "(max-width: 768px)" },
        { width: 500, condition: "(max-width: 992px)" },
        { width: 600, condition: "(min-width: 993px)" },
      ];

      const generateImageSet = (imageSrc) => {
        if (!imageSrc) return { src: "", srcset: "", sizes: "" };

        const isShopifyCDN = imageSrc.includes("shopifycdn.com") || imageSrc.includes("cdn.shopify.com");

        if (!isShopifyCDN) {
          return {
            src: imageSrc,
            srcset: imageSrc,
            sizes: "100vw",
          };
        }

        const baseUrl = imageSrc.split("?")[0];

        const srcsetEntries = [];
        breakpoints.forEach((bp) => {
          const height = Math.round(bp.width * (baseHeight / baseWidth));

          srcsetEntries.push(`${baseUrl}?width=${bp.width}&height=${height}&${cdnParams} ${bp.width}w`);

          const width2x = bp.width * 2;
          const height2x = height * 2;
          srcsetEntries.push(`${baseUrl}?width=${width2x}&height=${height2x}&${cdnParams} ${width2x}w`);
        });

        const defaultHeight = Math.round(300 * (baseHeight / baseWidth));
        const defaultSrc = `${baseUrl}?width=300&height=${defaultHeight}&${cdnParams}`;
        const sizes = ["(max-width: 480px) 100vw", "(max-width: 768px) 50vw", "(max-width: 992px) 33vw", "25vw"].join(", ");

        return {
          src: defaultSrc,
          srcset: srcsetEntries.join(", "),
          sizes: sizes,
        };
      };

      const primary = generateImageSet(primarySrc);
      const secondary = generateImageSet(secondarySrc);

      return {
        primarySrc: primary.src,
        primarySrcset: primary.srcset,
        secondarySrc: secondary.src,
        secondarySrcset: secondary.srcset,
        sizes: primary.sizes,
      };
    }

    getSmartPromoBadge(product) {
      // Skip badges for excluded products (e.g., gift cards)
      if (ConstructorUtils.isExcludedProduct && ConstructorUtils.isExcludedProduct(product)) {
        return { shouldShow: false, badgeHTML: null, badgeType: "excluded-product" };
      }

      const promoBadgeSettings = window.theme?.promoBadge;

      if (!promoBadgeSettings?.enabled) {
        return {
          shouldShow: false,
          badgeHTML: null,
          badgeType: "promo-disabled",
        };
      }

      const variant = product.variants?.[0] || product;
      const compareAtPrice = variant.compare_at_price || product.compare_at_price || 0;
      const price = variant.price || product.price || 0;

      if (!compareAtPrice || compareAtPrice <= price) {
        return { shouldShow: false, badgeHTML: null, badgeType: "no-discount" };
      }

      const savingsPercentage = Math.round(((compareAtPrice - price) * 100) / compareAtPrice);
      const remainder = savingsPercentage % 5;
      const roundedPercentage = remainder > 2 ? savingsPercentage + 5 - remainder : savingsPercentage - remainder;
      const opacity = (promoBadgeSettings.opacity || 100) / 100;
      const bgColor = promoBadgeSettings.bgColor || "#ff0000";
      const rgbMatch = bgColor.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
      const r = rgbMatch ? parseInt(rgbMatch[1], 16) : 255;
      const g = rgbMatch ? parseInt(rgbMatch[2], 16) : 0;
      const b = rgbMatch ? parseInt(rgbMatch[3], 16) : 0;

      const badgeHTML = `
        <div class="promo-badge-wrapper position-absolute" style="z-index: 1;">
          <span class="promo-badge badge"
                style="padding: ${promoBadgeSettings.padding || 8}px;
                       width: ${promoBadgeSettings.width || 60}px;
                       height: ${promoBadgeSettings.height || 60}px;
                       font-size: ${promoBadgeSettings.fontSize || 12}px;
                       background-color: rgba(${r}, ${g}, ${b}, ${opacity});
                       color: ${promoBadgeSettings.textColor || "#ffffff"};
                       border-radius: ${promoBadgeSettings.borderRadius || 50}px;
                       display: inline-flex;
                       align-items: center;
                       justify-content: center;
                       font-weight: 500;
                       text-transform: uppercase;
                       line-height: 1.2;">
            ${roundedPercentage}% OFF
          </span>
        </div>
      `;

      return {
        shouldShow: true,
        badgeHTML: badgeHTML,
        badgeType: "promo",
        discountPercentage: roundedPercentage,
      };
    }

    getSmartCustomBadge(product) {
      // Skip badges for excluded products (e.g., gift cards)
      if (ConstructorUtils.isExcludedProduct && ConstructorUtils.isExcludedProduct(product)) {
        return { shouldShow: false, badgeHTML: null };
      }

      const customBadgeSettings = window.theme?.customBadge;

      if (!customBadgeSettings?.globalEnabled || !customBadgeSettings?.marketSettings?.enabled) {
        return { shouldShow: false, badgeHTML: null };
      }

      const settings = customBadgeSettings.marketSettings;

      // Check for required tag filter
      const requiredTag = settings.tag;
      // Get tags from multiple possible sources
      const rawTags = product.tags || product.constructorData?.data?.tags || product.data?.tags || [];
      const tags = Array.isArray(rawTags) ? rawTags : typeof rawTags === "string" ? rawTags.split(",").map((t) => t.trim()) : [];
      const hasRequiredTag =
        !requiredTag || requiredTag.trim() === "" || tags.some((tag) => tag.toLowerCase() === requiredTag.toLowerCase().trim());

      if (!hasRequiredTag) {
        return { shouldShow: false, badgeHTML: null };
      }

      const badgeHTML = `
      <div class="custom-badge-wrapper position-absolute" style="z-index: 2;">
        <span class="custom-badge badge"  
              style="background-color: ${settings.bgColor}; 
                     color: ${settings.textColor}; 
                     font-size: ${settings.fontSize}px; 
                     border-radius: ${settings.borderRadius}px; 
                     opacity: ${settings.opacity / 100}; 
                     padding: ${settings.padding}px; 
                     width: ${settings.width}px; 
                     height: ${settings.height}px; 
                     display: flex; 
                     align-items: center; 
                     justify-content: center;">
          ${settings.text}
        </span>
      </div>
    `;

      return {
        shouldShow: true,
        badgeHTML: badgeHTML,
      };
    }

    _legacyGetSmartVariantSwatches(product, imgWidth, imgHeight) {
      // Skip swatches for excluded products (e.g., gift cards)
      if (ConstructorUtils.isExcludedProduct && ConstructorUtils.isExcludedProduct(product)) {
        return { shouldShow: false, swatchesHTML: null };
      }

      const swatchSettings = window.theme?.variantSwatches;

      if (!swatchSettings?.enabled) {
        return { shouldShow: false, swatchesHTML: null };
      }

      // Prefer transformed variants over raw variations (transformed variants have parsed option1/option2)
      const variations = product?.variants || product?.variations || [];

      if (!variations || variations.length === 0) {
        return { shouldShow: false, swatchesHTML: null };
      }

      const colorSet = new Set();
      variations.forEach((variation) => {
        const varData = variation.data || variation;

        // Get color from explicit fields first, then fallback to parsed options
        let color = varData.color || varData.colour || variation.option1;

        if (!color && varData.title) {
          const titleParts = varData.title.split(" / ");
          if (titleParts.length >= 2) {
            color = titleParts[1];
          } else {
            color = varData.title;
          }
        }

        if (color && color.trim()) {
          colorSet.add(color.trim());
        }
      });

      const swatchValues = Array.from(colorSet);

      if (swatchValues.length === 0) {
        return { shouldShow: false, swatchesHTML: null };
      }

      const maxVisible = swatchSettings.maxVisible || 4;

      let swatchesHTML = `
      <div class="swatches-container">
        <ul class="color-swatches list-unstyled mx-0 mb-5" aria-label="Color options">
    `;

      let visibleSwatchCount = 0;

      swatchValues.forEach((colorValue, index) => {
        let variantFound = null;

        for (const variation of variations) {
          const varData = variation.data || variation;
          const varColor = varData.color || varData.colour;
          const varTitle = varData.title || "";

          const colorSearch = colorValue.toLowerCase();

          if (varColor && varColor.toLowerCase() === colorSearch) {
            variantFound = varData;
            break;
          }

          if (varTitle) {
            const variantTitle = varTitle.toLowerCase();
            if (
              variantTitle.includes(colorSearch) ||
              variantTitle.endsWith(`/ ${colorSearch}`) ||
              variantTitle.startsWith(`${colorSearch} /`)
            ) {
              variantFound = varData;
              break;
            }
          }
        }

        const showSoldOut = swatchSettings.showSoldOut;

        let isVariantAvailable = true;
        if (variantFound) {
          if (variantFound.hasOwnProperty("available")) {
            isVariantAvailable = variantFound.available;
          } else if (variantFound.hasOwnProperty("inventory_quantity")) {
            isVariantAvailable = variantFound.inventory_quantity > 0;
          }
        }

        if (!showSoldOut && variantFound && !isVariantAvailable) {
          return;
        }

        const isHidden = visibleSwatchCount >= maxVisible ? "hidden" : "";
        visibleSwatchCount++;

        let variantImageUrl = product.data?.image_url || product.image_url || product.featured_image?.src || "";

        if (variantFound?.image_url) {
          variantImageUrl = variantFound.image_url;
        } else if (variantFound?.featured_image?.src) {
          variantImageUrl = variantFound.featured_image.src;
        } else if (variantFound?.image?.src) {
          variantImageUrl = variantFound.image.src;
        }

        if (variantImageUrl) {
          const targetW = imgWidth || 460;
          const targetH = imgHeight || 460;
          variantImageUrl = this.buildSizedImageUrl(variantImageUrl, targetW, targetH);
        }

        // Get price data from the variant
        const variantPrice = variantFound?.price || 0;
        const variantCompareAtPrice = variantFound?.compareAtPrice || variantFound?.compare_at_price || 0;
        const variantDiscountPercentage = variantFound?.discountPercentage || variantFound?.discount_percentage || 0;

        swatchesHTML += `
        <li class="p-1" ${isHidden}>
          <button class="btn${!isVariantAvailable ? " swatch-unavailable" : ""}" type="button"
                  data-img="${variantImageUrl}"
                  data-img-width="${imgWidth || 460}"
                  data-img-height="${imgHeight || 460}"
                  data-img-thumbnail="${window.theme?.productCard?.imgThumbnail || ""}"
                  data-variant-available="${isVariantAvailable}"
                  data-color-value="${colorValue}"
                  data-price="${variantPrice}"
                  data-compare-at-price="${variantCompareAtPrice}"
                  data-discount-percentage="${variantDiscountPercentage}"
                  onclick="onChangeConstructorProductSwatches(this, event)">
      `;

        if (swatchSettings.useVariantImages && variantImageUrl) {
          swatchesHTML += `
          <img class="img-fluid swatch-variant-image"
               src="${variantImageUrl}"
               alt="${colorValue}"
               width="40" height="40"
               loading="lazy" aria-hidden="true">
        `;
        } else {
          const swatchImageName = colorValue.toLowerCase().replace(/[\/\s]+/g, "-");
          const shopifySwatchUrl = `https://cdn.shopify.com/s/files/1/0262/9903/9821/files/swatch-${swatchImageName}.png?width=24&height=24&crop=center`;

          swatchesHTML += `
          <img class="img-fluid swatch-custom-image"
               src="${shopifySwatchUrl}"
               alt="${colorValue}"
               width="24" height="24"
               loading="lazy" aria-hidden="true"
               onerror="this.style.display='none'">
        `;
        }

        swatchesHTML += `
          </button>
        </li>
      `;
      });

      const showMoreButton = visibleSwatchCount > maxVisible;
      if (showMoreButton) {
        const hiddenCount = visibleSwatchCount - maxVisible;
        swatchesHTML += `
        <li class="p-0">
          <button class="btn btn-view-more" type="button"
                  onclick="showAllConstructorSwatchesInProduct(this, event)"
                  aria-label="+${hiddenCount} more color options">
            +${hiddenCount}
          </button>
        </li>
      `;
      }

      swatchesHTML += `
        </ul>
      </div>
    `;

      return {
        shouldShow: visibleSwatchCount > 0,
        swatchesHTML: swatchesHTML,
        colorCount: visibleSwatchCount,
      };
    }

    getSmartAddToCartForm(product) {
      const settings = window.theme?.productCard;

      if (!settings?.showAtcForm) {
        return "";
      }

      const isCustomizedProduct =
        product.collections?.some((collection) => collection.handle === "customized") ||
        product.tags?.some((tag) => tag === "customized");

      if (isCustomizedProduct) {
        return this.generateViewProductButton(product);
      }

      if (!product || !product.variants || product.variants.length === 0) {
        return "";
      }

      const availableVariants = product.variants.filter((variant) => variant.available);

      if (!product.available || availableVariants.length === 0) {
        return this.generateSoldOutButton();
      }

      // Single variant: show simple button; Multiple variants: show dropdown
      if (product.has_only_default_variant || availableVariants.length === 1) {
        return this.generateSimpleAddToCartButton(product, availableVariants[0]);
      }

      return this.generateDropdownAddToCartButton(product, availableVariants);
    }

    getSmartAddToCartFormMobile(product) {
      const settings = window.theme?.productCard;

      if (!settings?.showAtcForm) {
        return "";
      }

      const isCustomizedProduct =
        product.collections?.some((collection) => collection.handle === "customized") ||
        product.tags?.some((tag) => tag === "customized");

      if (isCustomizedProduct) {
        return this.generateViewProductButtonMobile(product);
      }

      if (!product || !product.variants || product.variants.length === 0) {
        return "";
      }

      const availableVariants = product.variants.filter((variant) => variant.available);

      if (!product.available || availableVariants.length === 0) {
        return this.generateSoldOutButtonMobile();
      }

      // Single variant: show simple button; Multiple variants: show dropdown
      if (product.has_only_default_variant || availableVariants.length === 1) {
        return this.generateSimpleAddToCartButtonMobile(product, availableVariants[0]);
      }

      return this.generateDropdownAddToCartButtonMobile(product, availableVariants);
    }

    generateViewProductButton(product) {
      const translations = window.theme?.productCard?.translations;
      const productUrl = product.url || `/products/${product.handle}`;

      return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <a href="${productUrl}" 
             class="view-product-btn-desktop"
             id="btn-vp-desktop"
             role="button">
            ${translations?.viewProduct || "View Product"}
          </a>
        </div>
      </div>
    `;
    }

    generateSoldOutButton() {
      const translations = window.theme?.productCard?.translations;

      return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <button class="btn-atc btn w-100 btn-primary rounded-pill" id="btn-atc-desktop" disabled>
            ${translations?.soldOut || "Sold Out"}
          </button>
        </div>
      </div>
    `;
    }

    generateSimpleAddToCartButton(product, variant) {
      const translations = window.theme?.productCard?.translations;

      return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form class="shopify-product-form product-item-form"
                onsubmit="onSubmitAtcForm(this, event)"
                data-product-id="${product.id}"
                data-product-title="${product.title}"
                data-product-url="${product.url || `/products/${product.handle}`}">
            <input type="hidden" name="id" value="${variant.id}">
            <button class="btn-atc btn btn-md w-100 btn-primary rounded-pill"
                    type="submit"
                    id="btn-atc-desktop"
                    name="add"
                    data-text-add-to-cart="${translations?.addToCart || "Add To Cart"}">
              ${translations?.addToCart || "Add To Cart"}
            </button>
          </form>
        </div>
      </div>
    `;
    }

    generateDropdownAddToCartButton(product, availableVariants) {
      const translations = window.theme?.productCard?.translations;
      const showInventory = window.theme?.productCard?.formShowInventory;

      const dropdownItems = availableVariants
        .map((variant) => this.generateVariantDropdownItem(variant, showInventory, translations))
        .join("");

      return `
      <div class="btn-atc-container">
        <div class="mb-2">
          <form method="post" action="/cart/add" id="product_form_${
            product.id
          }" accept-charset="UTF-8" class="shopify-product-form product-item-form" enctype="multipart/form-data" onsubmit="onSubmitAtcForm(this, event)" data-product-id="${
        product.id
      }" data-product-title="${product.title}" data-product-url="${product.url || `/products/${product.handle}`}">
            <input type="hidden" name="form_type" value="product">
            <input type="hidden" name="utf8" value="✓">
            <input type="hidden" name="id" value="${availableVariants[0].id}">
            <div class="w-200 dropup">
              <button type="button" class="btn-atc btn btn-md dropdown-toggle btn-primary w-200 rounded-pill" id="btn-atc-desktop" data-bs-toggle="dropdown" data-text-add-to-cart="Add To Cart" aria-expanded="false">
                Add To Cart
              </button>
              <ul class="dropdown-menu shadow w-200">
                ${dropdownItems}
              </ul>
            </div>
            <input type="hidden" name="product-id" value="${product.id}">
          </form>
        </div>
      </div>
    `;
    }

    generateVariantDropdownItem(variant, showInventory, translations) {
      const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

      return `
      <li class="deny">
        <button class="dropdown-item" onclick="onClickAtcFormVariant(this, event)" data-variant-id="${
          variant.id
        }" type="button" data-cnstrc-btn="add_to_cart">
          <div class="d-flex flex-column justify-content-start lh-sm">
            <span class="small">${variant.title}</span>
            ${inventoryDisplay || '<small class="text-success d-block">In stock</small>'}
          </div>
        </button>
      </li>
    `;
    }

    getInventoryDisplay(variant, showInventory, translations) {
      if (!showInventory) {
        return "";
      }

      if (variant.available) {
        if (!variant.inventory_quantity || variant.inventory_quantity <= 0 || variant.inventory_quantity >= 10) {
          return `<small class="text-success d-block">${translations?.inStock || "In stock"}</small>`;
        } else {
          const stockText = translations?.stockCount || "Only [count] left in stock";
          return `<small class="text-danger d-block">${stockText.replace("[count]", variant.inventory_quantity)}</small>`;
        }
      } else {
        return `<small class="text-muted d-block">${translations?.soldOut || "Sold out"}</small>`;
      }
    }

    generateViewProductButtonMobile(product) {
      const translations = window.theme?.productCard?.translations;
      const productUrl = product.url || `/products/${product.handle}`;

      return `
      <div class="mobile-view d-block d-lg-none">
        <a href="${productUrl}" 
           class="btn-vp w-100 d-block"
           id="new-view-product-btn-mobile"
           role="button">
          ${translations?.viewProduct || "View Product"}
        </a>
      </div>
    `;
    }

    generateSoldOutButtonMobile() {
      const translations = window.theme?.productCard?.translations;

      return `
      <div class="mobile-view d-block d-lg-none">
        <button class="btn-atc btn w-100 btn-primary rounded-pill" disabled>
          ${translations?.soldOut || "Sold Out"}
        </button>
      </div>
    `;
    }

    generateSimpleAddToCartButtonMobile(product, variant) {
      const translations = window.theme?.productCard?.translations;

      return `
      <div class="mobile-view d-block d-lg-none">
        <form class="shopify-product-form product-item-form"
              onsubmit="onSubmitAtcForm(this, event)"
              data-product-id="${product.id}"
              data-product-title="${product.title}"
              data-product-url="${product.url}">
          <input type="hidden" name="id" value="${variant.id}">
          <button class="btn-atc btn btn-md w-100 btn-primary rounded-pill"
                  type="submit"
                  name="add"
                  data-text-add-to-cart="${translations?.addToCart || "Add to cart"}">
            ${translations?.addToCart || "Add to cart"}
          </button>
        </form>
      </div>
    `;
    }

    generateDropdownAddToCartButtonMobile(product, availableVariants) {
      const translations = window.theme?.productCard?.translations;
      const showInventory = window.theme?.productCard?.formShowInventory;

      const variantItems = availableVariants
        .map((variant) => this.generateVariantDropdownItemMobile(variant, showInventory, translations))
        .join("");

      return `
      <div class="mobile-view mb-2">
        <form method="post" action="/cart/add" id="product_form_${product.id}" accept-charset="UTF-8" class="shopify-product-form product-item-form" enctype="multipart/form-data" onsubmit="onSubmitAtcForm(this, event)" data-product-id="${product.id}" data-product-title="${product.title}" data-product-url="${product.url}">
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="id" value="${availableVariants[0].id}">
          <div class="w-100 dropup">
            <button type="button" class="btn-atc w-100 rounded-pill" id="new-add-to-bag-btn-mobile" data-bs-toggle="dropdown" data-text-add-to-cart="Add To Cart" aria-expanded="false">
              Add To Cart
            </button>
            <ul class="dropdown-menu shadow w-100">
              ${variantItems}
            </ul>
          </div>
          <input type="hidden" name="product-id" value="${product.id}">
        </form>
      </div>
    `;
    }

    generateVariantDropdownItemMobile(variant, showInventory, translations) {
      const inventoryDisplay = this.getInventoryDisplay(variant, showInventory, translations);

      return `
      <li class="deny">
        <button class="dropdown-item" onclick="onClickAtcFormVariant(this, event)" data-variant-id="${
          variant.id
        }" type="button" data-cnstrc-btn="add_to_cart">
          <div class="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center lh-sm">
            <span class="small">${variant.title}</span>
            ${inventoryDisplay ? `<small class="text-success">${inventoryDisplay.replace(/<[^>]*>/g, "")}</small>` : ""}
          </div>
        </button>
      </li>
    `;
    }

    formatMoneyWithCurrency(cents) {
      return ConstructorUtils.formatMoneyWithCurrency(cents);
    }

    formatMoney(cents, format) {
      return ConstructorUtils.formatMoney(cents, format);
    }

    getCachedResult(key) {
      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
        return cached;
      }
      this.cache.delete(key);
      return null;
    }

    setCachedResult(key, data) {
      this.cache.set(key, data);
    }

    showLoadingState(section) {
      if (!section) return;

      const productList = section.querySelector(".product-list");
      if (productList) {
        productList.classList.add("constructor-loading");
        if (productList.children.length === 0) {
          const skeletonCount = Number(section.dataset.numResults) || 8;
          const frag = document.createDocumentFragment();
          for (let i = 0; i < skeletonCount; i++) {
            const li = document.createElement("li");
            li.className = "product-item splide__slide p-2 p-lg-3 mb-4 skeleton-card";
            li.innerHTML =
              '<div class="img-wrapper"><div class="skeleton skeleton-img"></div></div>' +
              '<div class="skeleton skeleton-line w-75 mx-auto mb-2"></div>' +
              '<div class="skeleton skeleton-line w-50 mx-auto"></div>';
            frag.appendChild(li);
          }
          productList.appendChild(frag);
        }
      }
    }

    hideLoadingState(section) {
      if (!section) return;

      const productList = section.querySelector(".product-list");
      if (productList) {
        productList.classList.remove("constructor-loading");
        productList.querySelectorAll(".skeleton-card").forEach((el) => el.remove());
      }
    }

    showErrorState(section) {
      if (!section) return;
      const shopifySection = section.closest(".shopify-section") || section.closest("section");
      if (shopifySection) shopifySection.style.display = "none";
    }

    showNoResults(section, podId) {
      if (!section) return;
      const shopifySection = section.closest(".shopify-section") || section.closest("section");
      if (shopifySection) shopifySection.style.display = "none";
    }

    initializeProductTracking(container, podId, products) {
      const productItems = container.querySelectorAll(".product-item");
      productItems.forEach((item, index) => {
        const productLink = item.querySelector('a[href*="/products/"]');
        if (productLink && this.client?.tracker) {
          const itemId = item.getAttribute("data-cnstrc-item-id");
          const itemName = item.getAttribute("data-cnstrc-item-name");
          const strategyId = item.getAttribute("data-cnstrc-strategy-id");
          const position = parseInt(item.getAttribute("data-cnstrc-position")) || index + 1;

          productLink.removeEventListener("click", this.handleProductClick);

          let touchStartY = 0;
          let touchStartTime = 0;
          let isScrolling = false;

          const handleTouchStart = (event) => {
            touchStartY = event.touches[0].clientY;
            touchStartTime = Date.now();
            isScrolling = false;
          };

          const handleTouchMove = (event) => {
            const touchCurrentY = event.touches[0].clientY;
            const deltaY = Math.abs(touchCurrentY - touchStartY);

            if (deltaY > 10) {
              isScrolling = true;
            }
          };

          const handleClick = (event) => {
            const timeSinceTouch = Date.now() - touchStartTime;

            if (isScrolling || (timeSinceTouch < 150 && touchStartTime > 0)) {
              return;
            }

            try {
              if (this.client?.tracker) {
                this.client.tracker.trackClick({
                  item_id: itemId,
                  item_name: itemName,
                  item_section: "Products",
                  position: position,
                  strategy_id: strategyId,
                  pod_id: podId,
                });
              }
            } catch (error) {
              console.warn("Constructor.io tracking error:", error);
            }
          };

          productLink.addEventListener("touchstart", handleTouchStart, { passive: true });
          productLink.addEventListener("touchmove", handleTouchMove, { passive: true });
          productLink.addEventListener("click", handleClick);

          productLink._constructorClickHandler = handleClick;
          productLink._constructorTouchStartHandler = handleTouchStart;
          productLink._constructorTouchMoveHandler = handleTouchMove;
        }
      });
    }

    cleanupProductTracking(container) {
      const productItems = container.querySelectorAll(".product-item");
      productItems.forEach((item) => {
        const productLink = item.querySelector('a[href*="/products/"]');
        if (productLink) {
          if (productLink._constructorClickHandler) {
            productLink.removeEventListener("click", productLink._constructorClickHandler);
            delete productLink._constructorClickHandler;
          }

          if (productLink._constructorTouchStartHandler) {
            productLink.removeEventListener("touchstart", productLink._constructorTouchStartHandler);
            delete productLink._constructorTouchStartHandler;
          }

          if (productLink._constructorTouchMoveHandler) {
            productLink.removeEventListener("touchmove", productLink._constructorTouchMoveHandler);
            delete productLink._constructorTouchMoveHandler;
          }
        }
      });
    }

    reinitializeComponents(container) {
      this.cleanupProductTracking(container);

      document.dispatchEvent(
        new CustomEvent("constructor:recommendations-loaded", {
          detail: { container },
        })
      );

      if (window.ProductCard) {
        container.querySelectorAll(".product-item").forEach((card) => {
          new ProductCard(card);
        });
      }
    }

    initializeSplideCarousel(container) {
      try {
        const existingSplides = container.querySelectorAll(".splide.is-initialized");
        existingSplides.forEach((element) => {
          element.classList.remove("is-initialized");

          if (element.splide) {
            try {
              element.splide.destroy();
            } catch (e) {}
            delete element.splide;
          }

          const arrows = element.querySelectorAll(".splide__arrow");
          arrows.forEach((arrow) => arrow.remove());
        });

        const splideElements = container.querySelectorAll(".splide");

        if (splideElements.length === 0) {
          return;
        }

        if (typeof Splide === "undefined") {
          splideElements.forEach((splideEl) => {
            this.fallbackToGrid(splideEl);
          });
          return;
        }

        splideElements.forEach((element, index) => {
          const slidesCount = element.querySelectorAll(".splide__slide").length;
          const currentBreakpoint = window.innerWidth;
          let perPageCurrent = Number(element.dataset.breakpointXxl);

          if (currentBreakpoint < 576) perPageCurrent = Number(element.dataset.breakpointXs);
          else if (currentBreakpoint < 768) perPageCurrent = Number(element.dataset.breakpointSm);
          else if (currentBreakpoint < 992) perPageCurrent = Number(element.dataset.breakpointMd);
          else if (currentBreakpoint < 1200) perPageCurrent = Number(element.dataset.breakpointLg);
          else if (currentBreakpoint < 1400) perPageCurrent = Number(element.dataset.breakpointXl);

          const shouldHaveArrows = slidesCount > perPageCurrent;

          const isMobile = window.innerWidth <= 575;

          const mySplide = new Splide(element, {
            arrows: !isMobile && shouldHaveArrows,
            pagination: false,
            easing: element.dataset.easing,
            speed: Number(element.dataset.speed),
            perMove: Number(element.dataset.perMove),
            autoplay: element.dataset.autoplay === "true",
            interval: Number(element.dataset.interval),
            per_move: Number(element.dataset.perMove),
            rewind: element.dataset.rewind === "true",
            mediaQuery: "min",
            breakpoints: {
              0: {
                perPage: Number(element.dataset.breakpointXs),
                padding: element.dataset.breakpointXsPartial === "true" ? { right: "16%" } : 0,
                arrows: false,
                pagination: false,
              },
              576: {
                perPage: Number(element.dataset.breakpointSm),
                padding: 0,
                arrows: shouldHaveArrows,
                pagination: element.dataset.pagination === "true",
              },
              768: { perPage: Number(element.dataset.breakpointMd), padding: 0 },
              992: { perPage: Number(element.dataset.breakpointLg), padding: 0 },
              1200: { perPage: Number(element.dataset.breakpointXl), padding: 0 },
              1400: {
                perPage: Number(element.dataset.breakpointXxl),
                padding: 0,
              },
            },
            direction: document.documentElement.getAttribute("dir"),
          });

          mySplide.on("mounted", () => {
            const slidesCount = element.querySelectorAll(".splide__slide").length;
            const arrowsCount = element.querySelectorAll(".splide__arrow").length;

            if (slidesCount > 1 && arrowsCount === 0) {
              try {
                mySplide.refresh();
              } catch (e) {}
            }

            setTimeout(() => {
              fixArrowsPos();
              fixPagination();
            }, 100);
          });

          mySplide.on("moved", () => {
            if (window.innerWidth <= 575) {
              return;
            }

            setTimeout(() => {
              const pagination = element.querySelector(".splide__pagination");
              if (pagination) {
                const activeDots = pagination.querySelectorAll(".is-active").length;
                if (activeDots !== 1) {
                  fixPagination();
                }
              }
            }, 50);
          });

          const fixArrowsPos = () => {
            let top = container.querySelector(".product-item-img")?.clientHeight / 2;

            if (container.querySelector(".product-item .card")) {
              top = container.querySelector(".card")?.clientHeight / 2;
            }

            container.querySelectorAll(".splide__arrow").forEach((arrow) => {
              arrow.style.top = `${top}px`;
            });
          };

          const fixPagination = () => {
            if (window.innerWidth <= 575) {
              return;
            }

            if (element.dataset.pagination === "true") {
              setTimeout(() => {
                const pagination = element.querySelector(".splide__pagination");
                if (pagination) {
                  const dots = pagination.querySelectorAll(".splide__pagination__page");
                  dots.forEach((dot, index) => {
                    if (index === 0) {
                      dot.classList.add("is-active");
                    } else {
                      dot.classList.remove("is-active");
                    }
                  });

                  if (pagination.children.length <= 1) {
                    pagination.style.display = "none";
                  } else {
                    pagination.style.display = "";
                  }
                }
              }, 100);
            }
          };

          mySplide.mount();

          element.splide = mySplide;

          element.classList.add("is-initialized");
        });
      } catch (error) {
        container.querySelectorAll(".splide").forEach((splideEl) => {
          this.fallbackToGrid(splideEl);
        });
      }
    }

    fallbackToGrid(splideElement) {
      const track = splideElement.querySelector(".splide__track");
      const list = splideElement.querySelector(".splide__list");

      if (track && list) {
        splideElement.classList.remove("splide");
        splideElement.classList.add("fallback-grid");

        track.classList.remove("splide__track");
        list.classList.remove("splide__list");
        list.classList.add("row", "g-3");

        const slides = list.querySelectorAll(".splide__slide");
        slides.forEach((slide) => {
          slide.classList.remove("splide__slide");
          slide.classList.add("col-6", "col-md-4", "col-lg-3");
        });
      }
    }

    injectConstructorCSS() {
      if (document.getElementById("constructor-recommendations-styles")) {
        return;
      }

      const style = document.createElement("style");
      style.id = "constructor-recommendations-styles";
      style.textContent = `
      .product-list.constructor-loaded .product-item,
      [data-cnstrc-recommendations] .product-item {
        position: relative;
      }

      /* Remove gray border from card - clean look */
      .product-list.constructor-loaded .product-item .card,
      [data-cnstrc-recommendations] .product-item .card {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
      }

      /* Swatch button styling - remove Bootstrap btn border */
      .product-list.constructor-loaded .color-swatches .btn,
      [data-cnstrc-recommendations] .color-swatches .btn,
      [data-constructor-recommendations] .color-swatches .btn {
        border: none !important;
        outline: none !important;
        background: transparent !important;
        padding: 0 !important;
        border-radius: 50% !important;
        overflow: hidden;
      }

      /* Image wrapper - use padding trick for aspect ratio (same as browse) */
      .product-list.constructor-loaded .img-wrapper,
      [data-cnstrc-recommendations] .img-wrapper {
        position: relative;
        width: 100%;
        overflow: hidden;
      }

      /* Aspect ratio via ::before padding trick (like browse) */
      .product-list.constructor-loaded .img-wrapper::before,
      [data-cnstrc-recommendations] .img-wrapper::before {
        content: "";
        display: block;
        padding-top: 100%; /* 1:1 aspect ratio for square images */
      }

      /* Link wrapper fills the container absolutely */
      .product-list.constructor-loaded .img-wrapper > a,
      [data-cnstrc-recommendations] .img-wrapper > a {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      /* Product images - ABSOLUTE positioning to fill container (like browse) */
      .product-list.constructor-loaded .product-item-img,
      [data-cnstrc-recommendations] .product-item-img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
      }

      /* Ensure proper button positioning on Constructor products */
      .product-list.constructor-loaded .btn-atc-container,
      [data-cnstrc-recommendations] .btn-atc-container {
        position: absolute;
        bottom: 10px;
        right: 10px;
        z-index: 10;
        opacity: 0;
        transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
      }

      .product-list.constructor-loaded .product-item:hover .btn-atc-container,
      [data-cnstrc-recommendations] .product-item:hover .btn-atc-container {
        opacity: 1;
        transform: translateY(0);
      }

      /* Button styling for Constructor products */
      .product-list.constructor-loaded #btn-atc-desktop,
      .product-list.constructor-loaded #btn-vp-desktop,
      .product-list.constructor-loaded .view-product-btn-desktop,
      [data-cnstrc-recommendations] #btn-atc-desktop,
      [data-cnstrc-recommendations] #btn-vp-desktop,
      [data-cnstrc-recommendations] .view-product-btn-desktop {
        position: relative;
        width: 100%;
        min-width: 105px;
        padding: 4px 10px;
        margin-top: 16px;
        font-size: 10px !important;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: center;
        background-color: #000000;
        color: #ffffff;
        border: none;
        border-radius: 24px !important;
        cursor: pointer;
        transition: all 0.3s ease-in-out;
        outline: none;
      }

      .product-list.constructor-loaded .btn-atc:hover,
      [data-cnstrc-recommendations] .btn-atc:hover {
        background-color: #444;
      }

      /* Price styling for Constructor products */
      .product-list.constructor-loaded .product-item-price-carrossel,
      [data-cnstrc-recommendations] .product-item-price-carrossel {
        text-align: center;
        font-size: 1rem;
        letter-spacing: 0.04em;
        line-height: 1.6;
        font-weight: bold !important;
      }

      /* Mobile responsive styles - Seamless card design */
      @media (max-width: 767px) {
        /* Ensure parent containers allow dropdown overflow */
        .product-list.constructor-loaded .product-item,
        [data-cnstrc-recommendations] .product-item {
          overflow: visible !important;
        }

        /* Use flexbox to reorder elements: Image → Button → Content */
        .product-list.constructor-loaded .product-item > div,
        [data-cnstrc-recommendations] .product-item > div {
          display: flex;
          flex-direction: column;
          overflow: visible !important;
        }

        /* Reorder: Image first, Mobile ATC second, Content third */
        .product-list.constructor-loaded .product-item .img-wrapper,
        [data-cnstrc-recommendations] .product-item .img-wrapper {
          order: 1;
        }

        .product-list.constructor-loaded .product-item .mobile-view,
        [data-cnstrc-recommendations] .product-item .mobile-view {
          order: 2;
        }

        .product-list.constructor-loaded .product-item .firstline-container,
        [data-cnstrc-recommendations] .product-item .firstline-container {
          order: 3;
        }

        .product-list.constructor-loaded .btn-atc-container,
        [data-cnstrc-recommendations] .btn-atc-container {
          display: none;
        }

        .product-list.constructor-loaded .btn-atc-containerMobile,
        [data-cnstrc-recommendations] .btn-atc-containerMobile {
          display: flex;
          flex-direction: column;
        }

        /* Image wrapper - part of unified card, rounded top only when NO badge, bottom always flat */
        .product-list.constructor-loaded .product-item .img-wrapper,
        [data-cnstrc-recommendations] .product-item .img-wrapper {
          margin-bottom: 0 !important;
          margin-top: 0 !important;
          padding-bottom: 0 !important;
          border-bottom: none;
          border-radius: 10px 10px 0 0 !important;
          overflow: hidden;
          background-color: transparent;
        }

        /* Override ::before padding on mobile to eliminate gap */
        .product-list.constructor-loaded .product-item .img-wrapper::before,
        [data-cnstrc-recommendations] .product-item .img-wrapper::before {
          padding-top: 100% !important;
        }

        .product-list.constructor-loaded .product-item .img-wrapper img,
        [data-cnstrc-recommendations] .product-item .img-wrapper img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          margin-bottom: 0 !important;
          border-radius: 10px 10px 0 0 !important;
        }

        /* When badge exists above, remove top border-radius from image */
        .product-list.constructor-loaded .product-item .img-wrapper.has-badge-above img,
        [data-cnstrc-recommendations] .product-item .img-wrapper.has-badge-above img {
          border-radius: 0 !important;
        }

        /* Link inside img-wrapper - absolute positioning like browse */
        .product-list.constructor-loaded .product-item .img-wrapper > a,
        [data-cnstrc-recommendations] .product-item .img-wrapper > a {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: block;
          line-height: 0;
          margin: 0;
          padding: 0;
        }
        
        /* Mobile ATC - seamlessly attached with negative margin */
        .product-list.constructor-loaded .product-item .mobile-view,
        [data-cnstrc-recommendations] .product-item .mobile-view {
          margin: 0 !important;
          margin-top: -1px !important;
          padding: 0 !important;
          border-top: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 0 0 16px 16px;
          overflow: visible;
          background-color: #fff;
          position: relative;
          z-index: 10;
        }

        .product-list.constructor-loaded .product-item #new-add-to-bag-btn-mobile,
        .product-list.constructor-loaded .product-item #new-view-product-btn-mobile,
        [data-cnstrc-recommendations] .product-item #new-add-to-bag-btn-mobile,
        [data-cnstrc-recommendations] .product-item #new-view-product-btn-mobile {
          position: relative;
          width: 100%;
          min-width: 105px;
          padding: 10px 16px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          text-align: center;
          background-color: #000000;
          color: #ffffff;
          border: none;
          border-top: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 0 0 15px 15px !important;
          margin: 0 !important;
          cursor: pointer;
          transition: all 0.3s ease-in-out;
          outline: none;
        }
        
        .product-list.constructor-loaded .product-item .mobile-view .btn-atc,
        .product-list.constructor-loaded .product-item .mobile-view .btn-vp,
        [data-cnstrc-recommendations] .product-item .mobile-view .btn-atc,
        [data-cnstrc-recommendations] .product-item .mobile-view .btn-vp {
          border-radius: 0 0 15px 15px !important;
          margin: 0 !important;
          border-top: 1px solid rgba(0, 0, 0, 0.08);
          width: 100%;
          padding: 14px 16px;
          font-size: 12px;
          font-weight: 600;
        }

        /* Form inside mobile-view should be full width */
        .product-list.constructor-loaded .product-item .mobile-view form,
        [data-cnstrc-recommendations] .product-item .mobile-view form {
          width: 100%;
          margin: 0;
        }

        /* Dropup container full width */
        .product-list.constructor-loaded .product-item .mobile-view .dropup,
        [data-cnstrc-recommendations] .product-item .mobile-view .dropup {
          width: 100%;
          position: static;
        }

        /* Ensure dropdown menus are visible and properly positioned */
        .product-list.constructor-loaded .product-item .dropdown-menu,
        [data-cnstrc-recommendations] .product-item .dropdown-menu {
          position: absolute;
          z-index: 1050;
        }

        /* Content area - below the card, no border/background */
        .product-list.constructor-loaded .product-item .firstline-container,
        [data-cnstrc-recommendations] .product-item .firstline-container {
          padding: 12px 4px 0 4px;
          background: transparent;
          border: none;
          border-radius: 0;
        }

        /* Mobile left-align: title, price, variants, labels */
        .product-list.constructor-loaded .product-item,
        [data-cnstrc-recommendations] .product-item {
          text-align: left;
        }

        .product-list.constructor-loaded .product-item-title,
        .product-list.constructor-loaded .product-item-title a,
        [data-cnstrc-recommendations] .product-item-title,
        [data-cnstrc-recommendations] .product-item-title a {
          text-align: left;
        }

        .product-list.constructor-loaded .product-item-price,
        .product-list.constructor-loaded [data-product-item-price],
        [data-cnstrc-recommendations] .product-item-price,
        [data-cnstrc-recommendations] [data-product-item-price] {
          text-align: left;
          justify-content: flex-start;
        }

        /* Mobile left-align for variant swatches - universal selectors */
        .product-item .color-swatches,
        .splide__slide .color-swatches,
        .product-list .color-swatches,
        .product-list.constructor-loaded .color-swatches,
        [data-cnstrc-recommendations] .color-swatches,
        [data-constructor-recommendations] .color-swatches {
          display: flex !important;
          justify-content: flex-start !important;
          align-items: center !important;
          padding-left: 0 !important;
          margin-left: 0 !important;
          margin-right: auto !important;
        }

        .product-item .swatches-container,
        .splide__slide .swatches-container,
        .product-list .swatches-container,
        .product-list.constructor-loaded .swatches-container,
        [data-cnstrc-recommendations] .swatches-container,
        [data-constructor-recommendations] .swatches-container {
          text-align: left !important;
          display: block !important;
        }

        .product-item .color-swatches li,
        .splide__slide .color-swatches li,
        .product-list .color-swatches li,
        .product-list.constructor-loaded .color-swatches li,
        [data-cnstrc-recommendations] .color-swatches li,
        [data-constructor-recommendations] .color-swatches li {
          margin-left: 0 !important;
        }

        .product-item .color-swatches li:first-child,
        .splide__slide .color-swatches li:first-child,
        .product-list .color-swatches li:first-child,
        .product-list.constructor-loaded .color-swatches li:first-child,
        [data-cnstrc-recommendations] .color-swatches li:first-child,
        [data-constructor-recommendations] .color-swatches li:first-child {
          padding-left: 0 !important;
        }

        .product-item .firstline-container,
        .product-item .firstline-container > div,
        .splide__slide .firstline-container,
        .splide__slide .firstline-container > div,
        .product-list .firstline-container,
        .product-list .firstline-container > div,
        .product-list.constructor-loaded .firstline-container,
        .product-list.constructor-loaded .firstline-container > div,
        [data-cnstrc-recommendations] .firstline-container,
        [data-cnstrc-recommendations] .firstline-container > div,
        [data-constructor-recommendations] .firstline-container,
        [data-constructor-recommendations] .firstline-container > div {
          text-align: left !important;
        }

        .product-list.constructor-loaded .fake-discount-message,
        [data-cnstrc-recommendations] .fake-discount-message {
          text-align: left;
        }

        .product-list.constructor-loaded .firstline-container,
        [data-cnstrc-recommendations] .firstline-container {
          text-align: left;
        }

      }

      /* Desktop styles */
      @media (min-width: 768px) {
        .product-list.constructor-loaded .mobile-view,
        [data-cnstrc-recommendations] .mobile-view {
          display: none;
        }
      }


      /* Image hover effect - Secondary image styling */
      .product-list.constructor-loaded .img-wrapper .product-item-img--secondary,
      [data-cnstrc-recommendations] .img-wrapper .product-item-img--secondary {
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
        transition: opacity 0.3s ease-in-out;
        z-index: 1;
      }

      .product-list.constructor-loaded .img-wrapper.has-hover-image:hover .product-item-img--secondary,
      [data-cnstrc-recommendations] .img-wrapper.has-hover-image:hover .product-item-img--secondary {
        opacity: 1;
      }

      .product-list.constructor-loaded .img-wrapper.has-hover-image:hover .product-item-img:not(.product-item-img--secondary),
      [data-cnstrc-recommendations] .img-wrapper.has-hover-image:hover .product-item-img:not(.product-item-img--secondary) {
        opacity: 0;
      }

      /* PNG background for product images */
      .product-list.constructor-loaded .png-background,
      [data-cnstrc-recommendations] .png-background {
        background-color:rgb(255, 255, 255);
        border-radius: 5px;
      }

      /* ==========================================================================
         BADGE INSIDE IMAGE - Tag style flush left (all screen sizes)
         ========================================================================== */

      /* Hide mobile badge above image - we use desktop badge wrapper for all sizes */
      .mobile-badge-above-image,
      .product-list.constructor-loaded .mobile-badge-above-image,
      [data-cnstrc-recommendations] .mobile-badge-above-image {
        display: none !important;
      }

      /* Show desktop badge wrapper on ALL screen sizes including mobile */
      .product-list.constructor-loaded .desktop-badge-wrapper,
      [data-cnstrc-recommendations] .desktop-badge-wrapper {
        display: block !important;
      }

      /* Badge positioning - tag style flush left (all screens) */
      .product-list.constructor-loaded .desktop-badge-wrapper .badge-wrapper,
      .product-list.constructor-loaded .desktop-badge-wrapper .img-badge-sale,
      [data-cnstrc-recommendations] .desktop-badge-wrapper .badge-wrapper,
      [data-cnstrc-recommendations] .desktop-badge-wrapper .img-badge-sale {
        position: absolute;
        top: 12px;
        left: 0;
        right: auto;
        z-index: 2;
      }

      /* Badge tag style - subtle rounded right side (all screens) */
      .product-list.constructor-loaded .desktop-badge-wrapper .badge,
      [data-cnstrc-recommendations] .desktop-badge-wrapper .badge {
        border-radius: 0 4px 4px 0 !important;
        padding: 6px 12px 6px 10px !important;
      }

      /* Rounded top corners on ALL product images - all screens */
      .product-list.constructor-loaded .product-item .img-wrapper,
      [data-cnstrc-recommendations] .product-item .img-wrapper {
        border-top-left-radius: 10px !important;
        border-top-right-radius: 10px !important;
        overflow: hidden !important;
      }

      .product-list.constructor-loaded .product-item .img-wrapper img,
      [data-cnstrc-recommendations] .product-item .img-wrapper img {
        border-top-left-radius: 10px !important;
        border-top-right-radius: 10px !important;
      }

      /* Mobile specific */
      @media (max-width: 767px) {
        .product-list.constructor-loaded .product-item .img-wrapper,
        [data-cnstrc-recommendations] .product-item .img-wrapper {
          margin-top: 0 !important;
        }

        /* Mobile badge positioning */
        .product-list.constructor-loaded .desktop-badge-wrapper .badge-wrapper,
        .product-list.constructor-loaded .desktop-badge-wrapper .img-badge-sale,
        [data-cnstrc-recommendations] .desktop-badge-wrapper .badge-wrapper,
        [data-cnstrc-recommendations] .desktop-badge-wrapper .img-badge-sale {
          top: 8px;
        }

        /* Mobile badge size - slightly smaller */
        .product-list.constructor-loaded .desktop-badge-wrapper .badge,
        [data-cnstrc-recommendations] .desktop-badge-wrapper .badge {
          padding: 4px 10px 4px 8px !important;
          font-size: 10px !important;
        }
      }
    `;

      document.head.appendChild(style);
    }
  }

  let globalTouchState = {
    startY: 0,
    startTime: 0,
    isScrolling: false,
  };

  document.addEventListener(
    "touchstart",
    (e) => {
      globalTouchState.startY = e.touches[0].clientY;
      globalTouchState.startTime = Date.now();
      globalTouchState.isScrolling = false;
    },
    { passive: true }
  );

  document.addEventListener(
    "touchmove",
    (e) => {
      const deltaY = Math.abs(e.touches[0].clientY - globalTouchState.startY);
      if (deltaY > 10) {
        globalTouchState.isScrolling = true;
      }
    },
    { passive: true }
  );

  window.isUserScrolling = () => {
    const timeSinceTouch = Date.now() - globalTouchState.startTime;
    return globalTouchState.isScrolling || (timeSinceTouch < 150 && globalTouchState.startTime > 0);
  };

  window.onChangeConstructorProductSwatches = (btn, event) => {
    if (window.isUserScrolling && window.isUserScrolling()) {
      return;
    }

    if (event.type === "click") {
      event.preventDefault();
    }

    const productItem = btn.closest(".product-item");
    if (!productItem) {
      return;
    }

    const allSwatchesInProduct = productItem.querySelectorAll(".color-swatches .btn");
    allSwatchesInProduct.forEach((swatch) => swatch.classList.remove("selected"));

    btn.classList.add("selected");

    const productImg = productItem.querySelector(".product-item-img--primary");
    if (productImg && btn.dataset.img) {
      const imgOrientation = window.theme?.productCard?.imgOrientation || "square";
      let targetWidth, targetHeight;
      switch (imgOrientation) {
        case "square":
          targetWidth = 460;
          targetHeight = 460;
          break;
        case "landscape":
          targetWidth = 200;
          targetHeight = 160;
          break;
        case "portrait":
          targetWidth = 160;
          targetHeight = 200;
          break;
        default:
          targetWidth = 460;
          targetHeight = 460;
      }

      const currentAttrWidth = parseInt(productImg.getAttribute("width"), 10);
      const currentAttrHeight = parseInt(productImg.getAttribute("height"), 10);
      if (currentAttrWidth && currentAttrHeight) {
        targetWidth = currentAttrWidth;
        targetHeight = currentAttrHeight;
      }

      const rawSrc = btn.dataset.img;
      const baseUrl = rawSrc.split("?")[0];

      productImg.src = `${baseUrl}?width=${targetWidth}&height=${targetHeight}`;
      productImg.srcset = `${baseUrl}?width=${targetWidth}&height=${targetHeight} 1x, ${baseUrl}?width=${
        targetWidth * 2
      }&height=${targetHeight * 2} 2x`;
      productImg.setAttribute("width", String(targetWidth));
      productImg.setAttribute("height", String(targetHeight));

      productImg.style.width = "100%";
      productImg.style.height = "auto";
      productImg.style.aspectRatio = "1/1";
      productImg.style.objectFit = "cover";
      productImg.style.objectPosition = "center";
      productImg.style.maxWidth = "100%";
      productImg.style.display = "block";

      const imgWrapper = productImg.closest(".img-wrapper");
      if (imgWrapper) {
        imgWrapper.style.overflow = "hidden";
        imgWrapper.style.position = "relative";
      }

      productImg.offsetHeight;

      productImg.addEventListener(
        "load",
        function () {
          this.setAttribute("width", String(targetWidth));
          this.setAttribute("height", String(targetHeight));
          this.style.width = "100%";
          this.style.height = "auto";
          this.style.aspectRatio = "1/1";
          this.style.objectFit = "cover";
        },
        { once: true }
      );
    }

    // Update price display
    updateSwatchPrice(productItem, btn);
  };

  window.showAllConstructorSwatchesInProduct = (btn, event) => {
    if (window.isUserScrolling && window.isUserScrolling()) {
      return;
    }

    if (event.type === "click") {
      event.preventDefault();
    }

    const swatchesList = btn.closest(".color-swatches");
    if (!swatchesList) {
      return;
    }

    swatchesList.querySelectorAll("li[hidden]").forEach((li) => {
      li.removeAttribute("hidden");
    });

    btn.remove();
  };

  function initializeConstructorRecommendations() {
    const shouldInitialize =
      document.querySelector("[data-constructor-recommendations]") || window.cnstrc?.recommendationsEnabled;

    if (shouldInitialize && !window.constructorRecommendations) {
      window.constructorRecommendations = new ConstructorRecommendations();
    }
  }

  window.quickLoadRecommendations = function (selector = ".product-list", podId = "minicart-upsell") {
    const container = document.querySelector(selector);
    if (!container) {
      return;
    }

    container.closest("[data-constructor-recommendations]") || container.setAttribute("data-constructor-recommendations", podId);

    loadConstructorRecommendations(podId);
  };

  const loadConstructorRecommendations = (podId) => {
    const container = document.querySelector(`[data-constructor-recommendations="${podId}"]`);
    if (container && window.constructorRecommendations) {
      window.constructorRecommendations.loadRecommendations(podId, {
        section: container,
        numResults: parseInt(container.dataset.numResults) || 8,
      });
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeConstructorRecommendations);
  } else {
    initializeConstructorRecommendations();
  }
})();
